<template>
  <div class="pin-login-step card-base fade-in">
    <div class="card-body p-4">
      <div class="container mb-2">
        <div class="row d-flex justify-content-center">
          <div class="input-animation">
            <label class="pin-input-label">
              <input
                class="input-amount"
                ref="passwordField"
                @keyup.enter="handleLogin"
                placeholder="Enter your PIN"
                :type="passwordFieldType"
                v-model.trim="password"
              >
            </label>

            <div class="show-forgot-row d-flex">
              <div class="show-password-container">
                <label class="show-password-checkbox">
                  <input type="checkbox" v-model="showPassword">
                  <span class="checkbox-text">Show PIN</span>
                </label>
              </div>
              <div class="forgot-pin mt-3">
                <a href="#" class="forgot-link" @click.prevent="resetPassword">Forgot PIN? Reset</a>
              </div>
            </div>
          </div>

          <div class="container button-pane input-animation">
            <div class="row text-center d-flex flex-column align-items-center">
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">1</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">2</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">3</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">4</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">5</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">6</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">7</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">8</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">9</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">0</button>
                <button @click="backspace" class="keypad-btn function-btn" type="button">
                  <font-awesome-icon icon="fa-solid fa-delete-left"/>
                </button>
              </div>
            </div>
          </div>

          <div class="text-center my-3 input-animation">
            <button :disabled="loading || !isPinValid" @click="handleLogin" type="submit" class="login-button btn-primary-lightning">
              <span class="button-text">Login</span>
              <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { responses } from '@/utils/responses';
import { Storage } from '@/services/Storage';
import { errorHandler } from '@/services/errorHandler';
import axios from 'axios';
import Swal from 'sweetalert2';
import { generateUUID } from '@/utils/uuid';

const MIN_PIN_LENGTH = 4;

export default {
  name: 'PinLoginStep',
  data() {
    return {
      password: '',
      loading: false,
      showPassword: false
    };
  },
  computed: {
    passwordFieldType() {
      return this.showPassword ? 'text' : 'password';
    },
    /**
     * Validates the current PIN value against the relaxed contract.
     *
     * Contract: any printable character(s) - no digit-only restriction -
     * with length at least `MIN_PIN_LENGTH`, and no whitespace. Trims
     * surrounding whitespace before evaluating so accidental space-bar
     * presses don't break the check. No upper length bound.
     *
     * Used as the single source of truth for both the Login button's
     * `:disabled` binding and the submit guard in `handleLogin()`.
     *
     * @returns {boolean} True iff the trimmed PIN is at least
     *   `MIN_PIN_LENGTH` non-whitespace characters.
     */
    isPinValid() {
      const trimmed = String(this.password || '').trim();
      return trimmed.length >= MIN_PIN_LENGTH
        && /^\S+$/.test(trimmed);
    }
  },
  async mounted() {
    // Ensure store is set
    const defaultStore = await Storage.getDefaultStore();
    if (defaultStore === null) {
      this.$emit('error', 'No store selected');
      return;
    }

    // Load users if not already loaded
    let hasUsers = await auth.hasUsers();
    if (!hasUsers) {
      try {
        await this.$store.dispatch('getAllUsers');
      } catch (e) {
        // Continue even if fetching users fails
      }
    }

    // Auto-focus PIN input
    await new Promise(resolve => setTimeout(resolve, 500));
    this.$refs.passwordField?.focus();
  },
  methods: {
    /**
     * Runs the full 3-step PIN/PIN-reset flow (matching Lytning Core's
     * `openResetPasswordAlert()` at `src/views/UserLoginView.vue:976`):
     *
     *   Step 1 - Prompt the cashier for their email.
     *   Step 2 - POST it to `${backOfficeTomcatApi}auth/${storeDB}` with
     *            `{ localid, email }`; the server emails an OTP.
     *   Step 3 - Prompt the cashier for that OTP, then for a new password.
     *   Step 4 - POST `{ localid, otp, newPassword }` to
     *            `${backOfficeTomcatApi}auth/${storeDB}/processResetPassword`,
     *            then show a success toast and `location.reload()` so the
     *            cashier can sign in with the new credentials.
     *
     * The URL segment is the dynamic `storeDB` from `Storage.getCurrentStoreDB()`
     * so the call follows whatever store is currently configured on the device
     * (no hardcoded `wholesale_demo`).
     *
     * Two-localid pattern (critical):
     *   The backend's `AuthController` performs an idempotency check on each
     *   incoming `localid`: if the same `localid` is seen twice (e.g. it was
     *   already inserted into `update_request_log` by step 2), the controller
     *   replays the cached response from `LocalId.getResponse()`. Because of
     *   a latent backend bug (`LocalId.java` reads `HttpCode` while writing
     *   `status_code`), that replay is returned with `HttpCode=0` and surfaces
     *   to the client as a 500 "PENDING" error - even though the original
     *   request never actually ran the password-change logic.
     *
     *   To avoid tripping that idempotency replay, this flow generates a
     *   **fresh** UUID for the second POST:
     *     - `localid`  - used only for the step-2 email request.
     *     - `otpLocalid` - generated immediately before step 5 and used on
     *                       the `processResetPassword` request.
     *   A brand-new localid guarantees `LocalId.getLocalId()` returns `null`
     *   on the second call, so `changePassword()` executes for real instead
     *   of being short-circuited by the cached replay. This mirrors the
     *   pattern already used in Lytning Core at `UserLoginView.vue:1034`
     *   and `:1055`.
     *
     * Errors go through `errorHandler.tomcatError(err)` only.
     *
     * @returns {Promise<void>} Resolves after the flow completes or the user
     *   cancels any of the three prompts.
     */
    async resetPassword() {
      try {
        // ---- Step 1: collect email ----
        const emailResult = await Swal.fire({
          title: 'Reset Password',
          text: 'Please enter your email address:',
          input: 'email',
          inputPlaceholder: 'Enter your email',
          showCancelButton: true,
          confirmButtonText: 'Submit',
          inputValidator: (value) => {
            if (!value) return 'Please enter your email address';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return 'Please enter a valid email address';
            return undefined;
          }
        });
        if (!emailResult.value) return;

        const email = String(emailResult.value).trim();
        const storeDB = await Storage.getCurrentStoreDB();
        if (!storeDB) {
          return responses.throwErr('Store not set up on this device.');
        }

        const localid = generateUUID();

        this.loading = true;
        Swal.showLoading();

        // ---- Step 2: POST email ----
        await axios.post(`${backOfficeTomcatApi}auth/${storeDB}`, { localid, email });

        await Swal.fire({
          icon: 'success',
          title: 'Email sent!',
          text: "We've sent a reset password link to your email.",
          confirmButtonText: 'OK'
        });

        // ---- Step 3: collect OTP ----
        const otpResult = await Swal.fire({
          title: 'Enter OTP',
          text: 'Please enter the OTP sent to your email:',
          input: 'text',
          inputPlaceholder: 'Enter OTP',
          showCancelButton: true,
          confirmButtonText: 'Next',
          inputValidator: (value) => (value ? undefined : 'Please enter the OTP')
        });
        if (!otpResult.value) return;

        // ---- Step 4: collect new password ----
        const passwordResult = await Swal.fire({
          title: 'New Password',
          text: 'Please enter your new password:',
          input: 'password',
          inputPlaceholder: 'Enter new password',
          showCancelButton: true,
          confirmButtonText: 'Reset',
          inputValidator: (value) => (value && value.length >= 4 ? undefined : 'Password must be at least 4 characters')
        });
        if (!passwordResult.value) return;

        // ---- Step 5: POST OTP + new password ----
        // Generate a fresh localid here so the backend's idempotency check
        // (AuthController -> LocalId.getLocalId) on `processResetPassword`
        // does not find the row inserted by step 2 and replay a 500
        // "PENDING" cached response. Mirrors Lytning Core's pattern at
        // UserLoginView.vue:1034 and :1055.
        const otpLocalid = generateUUID();
        await axios.post(
          `${backOfficeTomcatApi}auth/${storeDB}/processResetPassword`,
          { localid: otpLocalid, otp: otpResult.value, newPassword: passwordResult.value }
        );

        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Your password has been reset successfully.',
          confirmButtonText: 'OK'
        }).then(() => location.reload());
      } catch (err) {
        errorHandler.tomcatError(err);
      } finally {
        Swal.hideLoading?.();
        this.loading = false;
      }
    },

    /**
     * Dispatches `posLogin` to the Vuex store with the trimmed PIN.
     *
     * `isPinValid` is the single source of truth for both the Login button
     * gating and the submit guard. Failures (wrong PIN, network, 5xx) are
     * thrown by `posLogin` and surfaced via `errorHandler.tomcatError(err)`.
     *
     * @returns {Promise<void>}
     */
    async handleLogin() {
      if (!this.isPinValid) {
        responses.throwErr(`PIN must be at least ${MIN_PIN_LENGTH} characters.`);
        return;
      }
      this.loading = true;

      try {
        const res = await this.$store.dispatch('posLogin', this.password);
        console.log('[PinLoginStep] posLogin result', { isValid: res && res.isValid, username: res && res.username });

        const isAdmin = res.username === 'Admin';
        console.log('[PinLoginStep] isAdmin check', {
          username: res.username,
          usernameType: typeof res.username,
          isAdmin,
          willShowSupportPicker: isAdmin,
        });
        await this.$store.dispatch('recordlogin', res.username);

        this.$emit('next', {
          step: 'pin-complete',
          data: {
            isAdmin,
            username: res.username
          }
        });
      } catch (err) {
        console.error('[PinLoginStep] handleLogin error', err);
        try {
          await this.$store.dispatch('getAllUsers');
        } catch (_) {}
        errorHandler.tomcatError(err);
      } finally {
        this.loading = false;
      }
    },

    inputNumber(event) {
      let elem = event.target;
      if (elem) {
        let txt = elem.textContent || elem.innerText;
        if (txt) this.password += txt;
      }
    },

    backspace() {
      let password = this.password;
      let l = password.length;
      this.password = password.substring(0, l - 1);
      l = this.password.length;
      if (l === 0) {
        this.password = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pin-login-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 1;
}

.input-animation {
  opacity: 1;
  transform: translateY(0);
}

.pin-input-label {
  display: block;
  width: 100%;
}

.input-amount {
  width: 100%;
  display: block;
  margin: 0 auto;
  max-width: 350px;
  height: 56px;
  border-radius: 12px;
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  outline: transparent;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);

  &:focus {
    border-color: var(--accent-electric-blue);
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
    transform: translateY(-2px);
  }

  &:hover:not(:focus) {
    border-color: var(--accent-electric-blue);
  }
}

.show-password-container {
  display: flex;
  justify-content: center;
}

.show-forgot-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.show-password-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    color: var(--accent-electric-blue);

    .checkbox-text {
      color: var(--accent-electric-blue);
    }
  }
}

.checkbox-text {
  margin-left: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.forgot-pin {
  margin-bottom: 1rem;
}

.forgot-link {
  color: var(--accent-electric-blue);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-neon-purple);
  }
}

.button-pane {
  margin-top: 1.5rem;
}

.keypad-btn {
  display: inline-block;
  font-weight: 700;
  font-family: var(--font-family);
  font-size: 24px;
  color: var(--text-primary);
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: var(--card-bg);
  border: var(--card-border) !important;
  padding: .375rem .75rem;
  line-height: 1.5;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  height: 64px;
  width: 84px;
  margin: 5px;
  box-shadow: var(--card-shadow);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--card-shadow-hover);
    background-color: var(--secondary-button-bg);
  }

  &:active {
    transform: translateY(1px);
    background-color: var(--secondary-button-bg-dark);
  }

  &.function-btn {
    color: var(--accent-electric-blue);

    &:hover {
      color: var(--accent-neon-purple);
    }
  }
}

.login-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1rem auto 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
  }
}

/* Animations removed for compatibility with reduced motion settings */

/* Dark mode adjustments */
[data-theme='dark'] {
  .keypad-btn {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1) !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  .input-amount {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .keypad-btn {
    height: 56px;
    width: 72px;
    font-size: 20px;
    margin: 4px;
  }

  .card-body {
    padding: 1.5rem !important;
  }
}
</style>
